"use client";

/**
 * usePeopleWatching — session-aware hook for the "People Watching" urgency widget.
 *
 * Manages two independent product groups (Oestra & Face Cream), each with:
 * - Own simulated viewer/purchase counters (random, generated once per session).
 * - Own dismiss state persisted in sessionStorage.
 *
 * Oestra-specific behavior:
 * - Dismiss on LP → widget can still appear on PQP.
 * - Dismiss on PQP → widget hidden for the rest of the session.
 * - First PQP visit applies a one-time increment (+8 viewing, +5 purchased)
 *   and persists the new values so they never decrease on back-navigation.
 *
 * Face Cream: independent values, no increment, single dismiss flag.
 *
 * Feature flag: `urgency_widget_people_watching` (Optimizely).
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { usePathname } from "next/navigation";
import useFlag from "~/hooks/useFlag";
import { URGENCY_WIDGET_PEOPLE_WATCHING } from "~/constants/flags";
import {
    PAGE_CONFIG_MAP,
    VIEWING_RANGE,
    PURCHASED_RANGE,
    PQP_INCREMENT,
    SESSION_KEYS,
    type PageConfig,
} from "./config";

/** Returns a random integer in [min, max] inclusive. */
function randomInRange(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Reads a numeric value from sessionStorage or generates a new one in [min, max],
 * persists it, and returns the result. Ensures a stable value per session.
 */
function getOrCreateSessionValue(key: string, min: number, max: number): number {
    try {
        const stored = sessionStorage.getItem(key);
        const parsed = stored !== null ? parseInt(stored, 10) : NaN;
        if (Number.isFinite(parsed)) return parsed;

        const value = randomInRange(min, max);
        sessionStorage.setItem(key, String(value));
        return value;
    } catch {
        return randomInRange(min, max);
    }
}

/** Reads a boolean flag ("1" = true) from sessionStorage. */
function getSessionFlag(key: string): boolean {
    try {
        return sessionStorage.getItem(key) === "1";
    } catch {
        return false;
    }
}

/** Writes a boolean flag ("1") to sessionStorage. */
function setSessionFlag(key: string): void {
    try {
        sessionStorage.setItem(key, "1");
    } catch {
        /* noop */
    }
}

export interface PeopleWatchingState {
    /** Simulated "X people are viewing" counter. */
    viewingCount: number;
    /** Simulated "X women bought it today" counter. */
    purchasedCount: number;
    /** Current page configuration, or null if page is not eligible. */
    pageConfig: PageConfig | null;
    /** Whether the widget should be rendered. */
    isEnabled: boolean;
    /** Dismiss handler — hides widget and persists the state for the current group/pageType. */
    dismiss: () => void;
}

/**
 * Checks whether the Oestra widget was dismissed for the current page type.
 * LP and PQP have separate dismiss flags — closing on LP doesn't affect PQP.
 */
function isOestraDismissed(pageConfig: PageConfig): boolean {
    const keys = SESSION_KEYS.oestra;
    if (pageConfig.pageType === "lp") {
        return getSessionFlag(keys.DISMISSED_LP);
    }
    return getSessionFlag(keys.DISMISSED_PQP);
}

/** Checks whether the Face Cream widget was dismissed (single flag). */
function isFaceCreamDismissed(): boolean {
    return getSessionFlag(SESSION_KEYS.face_cream.DISMISSED);
}

export function usePeopleWatching(): PeopleWatchingState {
    const pathname = usePathname();
    const flag = useFlag(URGENCY_WIDGET_PEOPLE_WATCHING);

    const pageConfig = useMemo(() => {
        return PAGE_CONFIG_MAP[pathname] ?? null;
    }, [pathname]);

    const [isDismissed, setIsDismissed] = useState(() => {
        if (typeof window === "undefined" || !pageConfig) return true;
        if (pageConfig.group === "oestra") return isOestraDismissed(pageConfig);
        return isFaceCreamDismissed();
    });

    /**
     * Re-evaluate dismiss state whenever pageConfig changes (e.g. navigating
     * between Oestra LP → Face Cream PQP). Each group reads its own session keys.
     */
    useEffect(() => {
        if (typeof window === "undefined" || !pageConfig) {
            setIsDismissed(true);
            return;
        }
        if (pageConfig.group === "oestra") {
            setIsDismissed(isOestraDismissed(pageConfig));
        } else {
            setIsDismissed(isFaceCreamDismissed());
        }
    }, [pageConfig]);

    const [counters, setCounters] = useState({ viewingCount: 0, purchasedCount: 0 });
    const countersInitializedForRef = useRef<PageConfig | null>(null);

    useEffect(() => {
        if (typeof window === "undefined" || !pageConfig) {
            setCounters({ viewingCount: 0, purchasedCount: 0 });
            countersInitializedForRef.current = null;
            return;
        }
        if (countersInitializedForRef.current === pageConfig) return;
        countersInitializedForRef.current = pageConfig;

        if (pageConfig.group === "oestra") {
            const keys = SESSION_KEYS.oestra;
            let viewing = getOrCreateSessionValue(keys.VIEWING, VIEWING_RANGE.min, VIEWING_RANGE.max);
            let purchased = getOrCreateSessionValue(keys.PURCHASED, PURCHASED_RANGE.min, PURCHASED_RANGE.max);

            if (pageConfig.pageType === "pqp" && !getSessionFlag(keys.PQP_INCREMENTED)) {
                viewing += PQP_INCREMENT.viewing;
                purchased += PQP_INCREMENT.purchased;
                try {
                    sessionStorage.setItem(keys.VIEWING, String(viewing));
                    sessionStorage.setItem(keys.PURCHASED, String(purchased));
                    setSessionFlag(keys.PQP_INCREMENTED);
                } catch {
                    /* noop */
                }
            }
            setCounters({ viewingCount: viewing, purchasedCount: purchased });
        } else {
            const keys = SESSION_KEYS.face_cream;
            setCounters({
                viewingCount: getOrCreateSessionValue(keys.VIEWING, VIEWING_RANGE.min, VIEWING_RANGE.max),
                purchasedCount: getOrCreateSessionValue(keys.PURCHASED, PURCHASED_RANGE.min, PURCHASED_RANGE.max),
            });
        }
    }, [pageConfig]);

    const { viewingCount, purchasedCount } = counters;

    const dismiss = useCallback(() => {
        setIsDismissed(true);

        if (!pageConfig) return;

        if (pageConfig.group === "oestra") {
            if (pageConfig.pageType === "lp") {
                setSessionFlag(SESSION_KEYS.oestra.DISMISSED_LP);
            } else {
                setSessionFlag(SESSION_KEYS.oestra.DISMISSED_PQP);
            }
        } else {
            setSessionFlag(SESSION_KEYS.face_cream.DISMISSED);
        }
    }, [pageConfig]);

    const _isFlagEnabled = flag === "variant_a";
    const isEnabled = _isFlagEnabled && !!pageConfig && !isDismissed;

    return {
        viewingCount,
        purchasedCount,
        pageConfig,
        isEnabled,
        dismiss,
    };
}
